import React from 'react';

import styled from 'styled-components';

import { showPeriodByDay, showPeriodNoTime } from '@libs/NwMoment';

import EventTypeTag from '@components/EventDetails/EventTypeTag';

const EventSummaryContent = styled.div`
    min-width: 0;

    .event-item-main {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: 0.75rem;
        align-items: flex-start;
        min-width: 0;
    }

    .event-item-tag {
        padding-top: 0.1rem;
    }

    .event-item-body {
        min-width: 0;
    }

    .event-item-title-row,
    .event-item-meta-row {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 0.35rem 0.75rem;
        min-width: 0;
    }

    .event-item-meta-row {
        flex-direction: column;
        gap: 0.15rem;
    }

    .event-item-title {
        font-weight: 600;
        line-height: 1.25;
        min-width: 0;
    }

    .event-item-meta {
        color: ${(props) => props.theme.TextColor};
        font-size: ${(props) => props.theme.FontSizeSm};
        line-height: 1.25;
        white-space: nowrap;
    }

    .event-item-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
    }
`;

const getEventTitle = (event) => {
    return event.Name || event.Title || event.Service?.Name || 'Untitled event';
};

const getEventDatesPeriod = (event) => {
    const eventDates = [...(event.EventDates || [])].sort((a, b) => {
        return (a.FromDate || '').localeCompare(b.FromDate || '');
    });

    if (eventDates.length === 0) {
        return null;
    }

    const firstDate = eventDates[0];
    const lastDate = eventDates[eventDates.length - 1];

    return showPeriodByDay(firstDate.FromDate, lastDate.ToDate || lastDate.FromDate);
};

const getEventPeriod = (event) => {
    if (event.FirstDate || event.LastDate) {
        return showPeriodByDay(event.FirstDate, event.LastDate);
    }
    const eventDatesPeriod = getEventDatesPeriod(event);
    if (eventDatesPeriod) {
        return eventDatesPeriod;
    }
    if (event.CommonStartDate || event.CommonEndDate) {
        return showPeriodNoTime(event.CommonStartDate, event.CommonEndDate);
    }
    if (event.StartDateTime || event.ArrivalDateTime) {
        return showPeriodNoTime(event.StartDateTime, event.ArrivalDateTime);
    }
    if (event.FromDate || event.ToDate) {
        return showPeriodNoTime(event.FromDate, event.ToDate);
    }
    if (event.StartDate || event.EndDate) {
        return showPeriodNoTime(event.StartDate, event.EndDate);
    }
    return 'n.d.';
};

const getModelsCount = (event) => {
    const modelIds = new Set();

    for (const eventModel of event.EventModels || []) {
        modelIds.add(eventModel.ModelID || eventModel.ID || eventModel);
    }

    for (const eventDate of event.EventDates || []) {
        for (const eventDateModel of eventDate.EventDatesModels || []) {
            modelIds.add(eventDateModel.ModelID);
        }
    }

    for (const model of event.Models || []) {
        modelIds.add(model.ModelID || model.ID || model);
    }

    return modelIds.size;
};

const getDescription = (event) => {
    return event.Description || event.Note || null;
};

const ProjectEventSummary = ({ event, type }) => {
    const description = getDescription(event);
    const internalNote = event.InternalNote;
    const modelsCount = getModelsCount(event);
    const modelLabel = modelsCount === 1 ? 'model' : 'models';

    return (
        <EventSummaryContent>
            <div className="event-item-main">
                <div className="event-item-tag">
                    <EventTypeTag event={event} type={type || event.TypeName} />
                </div>
                <div className="event-item-body">
                    <div className="event-item-title-row">
                        <span className="event-item-title">{getEventTitle(event)}</span>
                        <span className="event-item-meta">{getEventPeriod(event)}</span>
                        <span className="event-item-meta">
                            {modelsCount} {modelLabel}
                        </span>
                    </div>
                    {(description || internalNote) && (
                        <div className="event-item-meta-row">
                            {description && (
                                <span className="event-item-meta event-item-text" title={description}>
                                    {description}
                                </span>
                            )}
                            {internalNote && (
                                <span className="event-item-meta event-item-text" title={internalNote}>
                                    <em>{internalNote}</em>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </EventSummaryContent>
    );
};

export default ProjectEventSummary;
